import Vue from 'vue'
import KeycloakAuthService from '@/services/KeycloakAuthService'
import CustomerService from '@/services/CustomerService'

const ADMIN_ID_ATTR = {
  hublot: 'hublot_admin_id',
  carnot: 'carnot_admin_id',
}
const APP_ATTR = {
  hublot: 'hublot_clientid',
  carnot: 'carnot_clientid',
}

import { getUserProfile } from '@/utils/Keycloak'

export default {
  state: {
    loading: false,
    targetCustomer: null,
    userProfile: null,
  },


  getters: {
    // Identifiant permanent de l'admin (dépend du contexte courant)
    hublotAdminId (state, _getters, rootState) {
      const app = rootState.home.app
      if (state.userProfile && state.userProfile.attributes) {
        const attr = state.userProfile.attributes[ADMIN_ID_ATTR[app]]
        return Array.isArray(attr) ? attr[0] : attr || null
      }
      return null
    },

    // clientId courant lu dans le profil utilisateur
    currentClientIdInToken (state, _getters, rootState) {
      const app = rootState.home.app
      if (state.userProfile && state.userProfile.attributes) {
        const attr = state.userProfile.attributes[APP_ATTR[app]]
        return Array.isArray(attr) ? attr[0] : attr || null
      }
      return null
    },



    impersonateLoading: (state) => state.loading,
    impersonateTargetCustomer: (state) => state.targetCustomer,

    isHublotAdmin (_state, getters) {
      return !!getters.hublotAdminId
    },

    isImpersonating (_state, getters) {
      const adminId = getters.hublotAdminId
      const clientId = getters.currentClientIdInToken
      return !!(adminId && clientId && String(adminId) !== String(clientId))
    },

    hasCurrentAccount (_state, getters) {
      return !!getters.currentClientIdInToken
    },
  },

  mutations: {
    setImpersonateLoading (state, loading) {
      state.loading = loading
    },
    setImpersonateTargetCustomer (state, customer) {
      state.targetCustomer = customer
    },
    setUserProfile (state, profile) {
      state.userProfile = profile
    },
  },

  actions: {
    loadUserProfile ({ commit, getters }) {
      return getUserProfile()
        .then(profile => {
          commit('setUserProfile', profile)
          const adminId = getters.hublotAdminId
          const clientId = getters.currentClientIdInToken
          if (adminId && clientId && String(adminId) !== String(clientId)) {
            return CustomerService.getById(clientId)
              .then(customer => {
                if (customer) {
                  commit('setImpersonateTargetCustomer', customer)
                  commit('setSelectedCustomer', customer)
                }
              })
              .catch(() => null)
          }
          return profile
        })
        .catch(() => commit('setUserProfile', null))
    },
    // Force le refresh du token Keycloak pour récupérer les nouveaux attributs
    refreshKeycloakToken () {
      const keycloak = Vue.prototype.$keycloak
      if (!keycloak || typeof keycloak.updateToken !== 'function') return Promise.resolve(false)
      // -1 force le refresh même si le token est encore valide
      return Promise.resolve(keycloak.updateToken(-1)).catch(() => false)
    },

    impersonateCustomer ({ commit, dispatch, rootState }, customer) {
      const app = rootState.home.app
      const clientId = customer?.clientId || customer?.clientid
      if (!clientId) {
        return Promise.reject(new Error('Aucun clientId trouvé pour ce client'))
      }

      commit('setImpersonateLoading', true)
      commit('setImpersonateTargetCustomer', customer)

      return KeycloakAuthService.impersonate(clientId, app)
        .then(() => dispatch('refreshKeycloakToken'))
        .then(() => dispatch('loadUserProfile'))
        .then(() => {
          dispatch('addNotification', {
            text: `Impersonation active sur ${customer?.name || clientId}`,
            color: 'success',
          })
        })
        .catch((error) => {
          dispatch('addNotification', {
            text: `Échec de l'impersonation : ${error.message}`,
            color: 'error',
          })
          throw error
        })
        .finally(() => commit('setImpersonateLoading', false))
    },

    stopImpersonate ({ commit, dispatch, rootState }) {
      const app = rootState.home.app
      commit('setImpersonateLoading', true)

      return KeycloakAuthService.stopImpersonate(app)
        .then(() => dispatch('refreshKeycloakToken'))
        .then(() => dispatch('loadUserProfile'))
        .then(() => {
          commit('setImpersonateTargetCustomer', null)
          dispatch('addNotification', {
            text: 'Impersonation terminée',
            color: 'success',
          })
        })
        .catch((error) => {
          dispatch('addNotification', {
            text: `Impossible d'arrêter l'impersonation : ${error.message}`,
            color: 'error',
          })
          throw error
        })
        .finally(() => commit('setImpersonateLoading', false))
    },

    removeOwnAccount ({ commit, dispatch, rootState }) {
      const app = rootState.home.app
      commit('setImpersonateLoading', true)

      return KeycloakAuthService.removeOwnAccount(app)
        .then(() => dispatch('refreshKeycloakToken'))
        .then(() => dispatch('loadUserProfile'))
        .then(() => {
          commit('setImpersonateTargetCustomer', null)
          commit('setSelectedCustomer', {})
          dispatch('addNotification', {
            text: `Compte ${app} supprimé`,
            color: 'success',
          })
        })
        .catch((error) => {
          dispatch('addNotification', {
            text: `Impossible de supprimer le compte : ${error.message}`,
            color: 'error',
          })
          throw error
        })
        .finally(() => commit('setImpersonateLoading', false))
    },

    restoreOwnAccount ({ commit, dispatch, rootState }) {
      const app = rootState.home.app
      commit('setImpersonateLoading', true)

      return KeycloakAuthService.stopImpersonate(app)
        .then(() => dispatch('refreshKeycloakToken'))
        .then(() => dispatch('loadUserProfile'))
        .then(() => {
          dispatch('addNotification', {
            text: `Compte ${app} restauré`,
            color: 'success',
          })
        })
        .catch((error) => {
          dispatch('addNotification', {
            text: `Impossible de restaurer le compte : ${error.message}`,
            color: 'error',
          })
          throw error
        })
        .finally(() => commit('setImpersonateLoading', false))
    },
  },
}
